/**
 * Every locale the studio publishes in.
 *
 * The array is the source and the union is derived from it, so the two cannot
 * disagree: a locale added to one and not the other is the failure this shape
 * removes. The design's language picker shows four (en/ja/ko/zh) while the
 * content is translated into all four — see the README.
 */
export const locales = ['en', 'ja', 'ko', 'zh'] as const;

export type Locale = (typeof locales)[number];

/** The locale everything else is a translation of. */
export const defaultLocale: Locale = 'en';

/** Each language named in itself, which is how a language picker should read. */
export const localeLabels: Record<Locale, string> = {
  en: 'English',
  ja: '日本語',
  ko: '한국어',
  zh: '中文',
};

/**
 * Every platform an app can be released on.
 *
 * ⚠️ **A mirror, not the source.** The source of truth is the `Platform` enum
 * in the API's `studio-app.entity.ts` — anh Duy chốt. A platform is added
 * there first and copied here second; if the two ever disagree, this file is
 * the one that is wrong.
 *
 * Copied at all because the API cannot import this library — see the note on
 * `LOCALES` in `locale-progress.ts` for why, at length. And written as an array
 * rather than a bare union because something has to be able to *read* the list
 * at run time in order to compare the two: a union does not exist once the code
 * is compiled, which is exactly why these copies could drift with nothing to
 * notice. `platform.spec.ts` in the API is what compares them now.
 *
 * Apple's spelling, because it is what the App Store prints and what the
 * website renders.
 */
export const platforms = ['macOS', 'iPhone', 'iPad'] as const;

export type Platform = (typeof platforms)[number];

export interface Timestamped {
  createdAt: string;
  updatedAt: string;
}

export interface Paginated<T> {
  items: T[];
  total: number;
}

/** A value carried in every supported language; `en` is always present. */
export type Translated<T> = { en: T } & Partial<Record<Locale, T>>;
